/**
 * OQ-315 · Tự đăng ký tài khoản.
 *
 * **Chủ dự án**: *"phát triển tiếp cho tôi trang đăng ký nữa"*.
 *
 * ── Màn hình sau khi bấm Đăng ký nói gì ────────────────────────────────────
 *
 * Máy chủ cố tình trả lời y hệt nhau cho email đã có và chưa có tài khoản, để
 * trang này không trở thành công cụ dò danh sách người dùng. Nên trang cũng
 * phải nói y hệt nhau — nếu nó hiện "đã gửi thư" cho email mới và một thông báo
 * khác cho email cũ thì công sức ở máy chủ thành vô nghĩa.
 *
 * Câu chốt vì thế nói về HỘP THƯ chứ không về tài khoản: *"nếu địa chỉ này chưa
 * có tài khoản, bạn sẽ nhận được thư"*. Đúng trong cả hai trường hợp, và người
 * thật vẫn biết phải đi đâu tiếp.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError, ensureCsrfCookie, useLoiApi } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useT } from '../../lib/i18n';
import type { MeResponse } from '../../lib/types';
import { Field } from '../../components/Field';
import { PasswordStrength } from '../../components/PasswordStrength';
import { TurnstileWidget } from '../../components/TurnstileWidget';
import { AuthLayout, FormAlert } from './AuthLayout';

export function RegisterPage() {
  const t = useT();

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [daGui, setDaGui] = useState(false);
  const { error, setError, xoaLoiKhiGo } = useLoiApi();

  /*
   * Captcha (OQ-316) — máy chủ quyết định có cần hay không, trang chỉ tuân theo.
   *
   * Trang tự đoán sẽ lệch với máy chủ ở đúng những lúc quan trọng: bật công tắc
   * mà chưa dán khoá, hoặc vừa tắt captcha xong. Lệch kiểu nào cũng tệ — người
   * dùng kẹt trước một ô không qua được, hoặc bấm gửi rồi bị từ chối vì thiếu
   * thứ mà trang chưa từng hỏi.
   */
  const [capReq, setCapReq] = useState(false);
  const [siteKey, setSiteKey] = useState('');
  const [capToken, setCapToken] = useState('');
  /* `null` = chưa hỏi xong. Ba trạng thái chứ không phải hai: vẽ "đã đóng" trong
     lúc còn đang hỏi là nói dối người dùng trong nửa giây. */
  const [choDangKy, setChoDangKy] = useState<boolean | null>(null);

  useEffect(() => {
    let song = true;
    void (async () => {
      try {
        const kq = await api.get<{
          enabled?: boolean;
          captcha?: { required: boolean; siteKey: string };
        }>('/auth/signup');
        if (!song) return;
        setChoDangKy(kq.enabled !== false);
        setCapReq(kq.captcha?.required === true);
        setSiteKey(kq.captcha?.siteKey ?? '');
      } catch {
        /* Không hỏi được thì cứ vẽ biểu mẫu; máy chủ vẫn là nơi phán quyết. */
        if (song) setChoDangKy(true);
      }
    })();
    return () => {
      song = false;
    };
  }, []);

  /* Cùng cách của InvitePage (OQ-243): suy từ giá trị hiện tại, không giữ cờ —
     cờ chỉ đổi lúc submit nên nó ở lại đỏ sau khi người ta đã gõ đúng. */
  const lech = confirm.length > 0 && password !== confirm;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirm) return;
    setBusy(true);
    setError(null);
    try {
      await ensureCsrfCookie();
      await api.post('/auth/signup', {
        email,
        fullName,
        password,
        ...(capToken ? { captchaToken: capToken } : {}),
      });
      setDaGui(true);
    } catch (err) {
      if (err instanceof ApiError) setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (daGui) {
    return (
      <AuthLayout
        title={t('auth.register.sentTitle')}
        lede={t('auth.register.sentLede', { email })}
        foot={
          <Link className="small" to="/login">
            {t('auth.2fa.backToLogin')}
          </Link>
        }
      >
        <div className="form-alert ok" role="status" data-od-id="register-sent">
          <span>{t('auth.register.sentHint')}</span>
        </div>
      </AuthLayout>
    );
  }

  /*
   * Hệ thống đang đóng đăng ký thì đừng vẽ biểu mẫu (OQ-322).
   *
   * Link ở màn đăng nhập đã ẩn, nhưng người gõ thẳng `/register` hoặc mở lại một
   * thẻ cũ vẫn tới được đây. Cho họ điền đủ bốn ô rồi mới nói "không nhận đăng
   * ký" là lãng phí công của họ — và câu ấy lại rơi vào ô Email như thể chính
   * địa chỉ đó có vấn đề.
   */
  if (choDangKy === false) {
    return (
      <AuthLayout
        title={t('auth.register.title')}
        foot={
          <Link className="small" to="/login">
            {t('auth.2fa.backToLogin')}
          </Link>
        }
      >
        <div className="form-alert" role="note" data-od-id="register-closed">
          <span>{t('auth.register.closed')}</span>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth.register.title')}
      lede={t('auth.register.lede')}
      foot={
        <Link className="small" to="/login">
          {t('auth.register.haveAccount')}
        </Link>
      }
    >
      <form
        className="stack"
        onChange={xoaLoiKhiGo.onChange}
        onSubmit={submit}
        data-od-id="form-register"
      >
        {error ? <FormAlert>{error.moTaRoRang}</FormAlert> : null}

        <Field
          label={t('auth.login.email')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          autoFocus
          required
          error={error?.fieldMessage('email')}
          data-od-id="register-email"
        />

        <Field
          label={t('auth.invite.fullName')}
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          autoComplete="name"
          required
          minLength={2}
          error={error?.fieldMessage('fullName')}
          data-od-id="register-fullname"
        />

        <Field
          label={t('auth.reset.newPassword')}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          required
          /*
           * Đọc CẢ HAI tên trường (OQ-321).
           *
           * Cùng một ô nhưng máy chủ gọi nó bằng hai tên khác nhau tuỳ chặng:
           * `password` khi lược đồ `zod` từ chối (bỏ trống), `newPassword` khi
           * chính sách mật khẩu từ chối (ngắn quá, thiếu chữ hoa, chứa tên…).
           *
           * Chỉ đọc một tên thì lỗi của chặng kia rơi mất, và người dùng đọc
           * được đúng một dòng "Dữ liệu không hợp lệ" ở trên cùng — câu không
           * nói gì. Chủ dự án đã mất công đoán mới ra: *"lỗi của mật khẩu nhưng
           * không báo rõ ràng"*.
           */
          error={error?.fieldMessage('newPassword') ?? error?.fieldMessage('password')}
          trailing={<PasswordStrength value={password} />}
          data-od-id="register-password"
        />

        <Field
          label={t('auth.reset.confirmPassword')}
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
          required
          error={lech ? t('auth.mismatch') : undefined}
          data-od-id="register-confirm"
        />

        {capReq && siteKey ? (
          <div className="stack" style={{ gap: 6 }}>
            <TurnstileWidget siteKey={siteKey} onToken={setCapToken} />
            {/* Lỗi captcha không thuộc ô nhập nào, nên nếu không hiện ở đây thì
                nó chỉ còn tồn tại trong dòng đỏ chung.

                Dùng `.form-alert.bad` chứ KHÔNG dùng `.err`: CSS quy định
                `.field .err { display:none }` và chỉ bật lên dưới `.field.invalid`
                — đặt `.err` ngoài cấu trúc ấy là viết một dòng chữ vô hình.
                `oq-221-lop-bi-an.test.ts` bắt được đúng chuyện này. */}
            {error?.fieldMessage('captchaToken') ? (
              <div className="form-alert bad" role="alert" data-od-id="register-captcha-error">
                <span>{error.fieldMessage('captchaToken')}</span>
              </div>
            ) : null}
          </div>
        ) : null}

        <button
          className="btn btn-primary btn-block"
          type="submit"
          disabled={busy || lech || (capReq && !capToken)}
          data-od-id="register-submit"
        >
          {busy ? t('common.processing') : t('auth.register.submit')}
        </button>
      </form>
    </AuthLayout>
  );
}

/**
 * Bấm liên kết trong thư xác minh → kích hoạt và vào thẳng.
 *
 * Tự gửi ngay khi mở, không bắt bấm thêm một nút nữa: người ta vừa bấm một nút
 * trong hộp thư rồi, và ở đây không còn quyết định nào để họ cân nhắc.
 */
export function VerifySignupPage() {
  const t = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setSession } = useAuth();
  const token = params.get('token') ?? '';

  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        await ensureCsrfCookie();
        const me = await api.post<MeResponse>('/auth/signup/verify', { token });
        if (!alive) return;
        setSession(me);
        navigate('/', { replace: true });
      } catch (err) {
        if (alive && err instanceof ApiError) setError(err);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token, navigate, setSession]);

  return (
    <AuthLayout
      title={t('auth.verifySignup.title')}
      foot={
        <Link className="small" to="/login">
          {t('auth.2fa.backToLogin')}
        </Link>
      }
    >
      {error ? (
        <FormAlert>{error.message}</FormAlert>
      ) : (
        <p className="lede" data-od-id="verify-signup-working">
          {t('auth.verifySignup.working')}
        </p>
      )}
    </AuthLayout>
  );
}
